import { Tag, TableToolbar, TableToolbarContent } from "@carbon/react";
import { NullableTableColumns } from "@flaminem/ui-components";
import { FC, useCallback, useMemo, useState } from "react";

import { PassiveModal } from "src/components/Modal";
import { TableTruncate } from "src/components/Tables";
import { FixedTable } from "src/components/Tables/FixedTable";
import { TableToolbarSearch } from "src/components/Tables/TableToolbarSearch";
import { useClientSchemaQuery } from "src/graphql/anti-fraud/queries/cases/GetCaseSchemasQuery";
import { useFormatPropertyValue } from "src/hooks/property/useFormatProperty";
import { useT } from "src/internationalization";
import { arePropertyValuesEqual } from "src/services/property/arePropertyValuesEqual";
import { CaseSummary } from "src/types/case";

import { CasePropertyDiff } from "./AlertCaseVersionsDiffModal.types";
import { getPropertyName, searchInCasePropertyDiff } from "./CaseVersionsDiffModal.utils";
import { VersionConfig } from "./CaseVersionsDiffTable.types";
import { getCaseVersionsPropertyDiffsFromSchema } from "./getCaseVersionsPropertyDiffsFromSchema";
import { hasCaseVersionsPropertyChanged } from "./hasPropertyChanged";

export type CaseVersionsDiffTableProps = {
  versions: VersionConfig[];
  title: string;
  subtitle?: string;
  onClose: () => void;
  searchInputId?: string;
  tableId?: string;
};

/**
 * Generic component for comparing case versions sequentially.
 * Takes a complete array of versions and compares each version with the previous one.
 * The first version in the array is the baseline (original).
 * Compares: v1 vs v0, v2 vs v1, v3 vs v2, etc.
 */
export const CaseVersionsDiffTable: FC<CaseVersionsDiffTableProps> = ({
  versions,
  title,
  subtitle,
  onClose,
  searchInputId = "searchCaseVersionsDiff",
  tableId = "caseVersionsDiff",
}) => {
  const t = useT();
  const formatPropertyValue = useFormatPropertyValue();
  const [searchTerm, setSearchTerm] = useState("");
  const { data: clientSchema } = useClientSchemaQuery();

  // The first version is the baseline (original)
  const originalCase = useMemo(() => {
    if (versions.length === 0) {
      throw new Error("At least one version is required");
    }
    const firstVersion = versions[0];
    if (!firstVersion.case) {
      throw new Error("The first version must have a case");
    }
    return firstVersion.case;
  }, [versions]);

  // Build versions object for getCaseVersionsPropertyDiffsFromSchema
  // Skip the first version as it's the baseline
  const versionsObject = useMemo(() => {
    const obj: Record<string, CaseSummary | null> = {};
    // Start from index 1, as index 0 is the baseline
    for (let i = 1; i < versions.length; i++) {
      const version = versions[i];
      obj[version.key] = version.case;
    }
    return obj;
  }, [versions]);

  // Version order for sequential comparison (skip the first one as it's the baseline)
  const versionOrder = useMemo(() => versions.slice(1).map((v) => v.key), [versions]);

  const rows = useMemo(() => {
    if (!clientSchema) return [];

    return getCaseVersionsPropertyDiffsFromSchema(
      originalCase,
      versionsObject,
      clientSchema,
      versionOrder
    ).filter(hasCaseVersionsPropertyChanged);
  }, [originalCase, versionsObject, clientSchema, versionOrder]);

  // Helper function to render a version cell with sequential comparison
  const renderVersionCell = useCallback(
    (row: CasePropertyDiff<string>, versionKey: string, versionIndex: number) => {
      const version = row.versions[versionKey as keyof typeof row.versions];
      const originalProperty = row.original.property;

      // Sequential comparison logic:
      // - versionIndex 1: compare v1 with v0 (baseline/original)
      // - versionIndex 2: compare v2 with v1 (previous version)
      // - versionIndex 3: compare v3 with v2 (previous version)
      // etc.
      //
      // getCaseVersionsPropertyDiffsFromSchema already handles sequential comparison correctly
      // by using versionsProperties[previousKey] which contains the actual property value
      // even if the previous version was "unchanged". So we can rely on changeType.
      //
      // For fallback calculation (when changeType is not available), we need to determine
      // the base property by finding the actual value of the previous version.
      let baseProperty: typeof originalProperty = originalProperty;
      if (versionIndex > 1) {
        // Find the actual property value of the previous version
        // If previous version had a change, use its property from row.versions
        // Otherwise, recursively find the value by checking earlier versions
        const previousVersionKey = versions[versionIndex - 1].key;
        const previousVersion = row.versions[previousVersionKey as keyof typeof row.versions];

        if (previousVersion?.property) {
          // Previous version had a change, use its property
          baseProperty = previousVersion.property;
        } else {
          // Previous version was unchanged, so it has the same value as its base
          // Recursively find the base by checking earlier versions
          // Start from the previous version and go back until we find a changed version or reach baseline
          for (let i = versionIndex - 1; i >= 1; i--) {
            const earlierVersionKey = versions[i].key;
            const earlierVersion = row.versions[earlierVersionKey as keyof typeof row.versions];
            if (earlierVersion?.property) {
              baseProperty = earlierVersion.property;
              break;
            }
            // If earlierVersion is null, it means it was unchanged, so continue to the next earlier version
          }
          // If we didn't find any changed version, use originalProperty (baseline)
        }
      }
      // If versionIndex === 1, baseProperty is already set to originalProperty (baseline)

      // If version is null but originalProperty exists, it means the property is unchanged
      // (version is null when changeType === "unchanged" in getCaseVersionsPropertyDiffsFromSchema)
      const versionProperty =
        version?.property ?? (version === null && baseProperty ? baseProperty : null);
      const formattedValue = versionProperty ? formatPropertyValue(versionProperty) : null;

      // Use the changeType if available, otherwise calculate it
      const changeType = version?.changeType;
      let isDeletion = false;
      let isAddition = false;
      let isModified = false;

      // Only determine change type if version exists (if version is null, property is unchanged)
      if (version !== null) {
        if (changeType) {
          // Use the changeType from getCaseVersionsPropertyDiffsFromSchema
          isDeletion = changeType === "deletion";
          isAddition = changeType === "addition";
          isModified = changeType === "modification";
        } else {
          // Fallback: calculate change type by comparing with base (previous version or original)
          const baseHasProperty = baseProperty !== null && baseProperty !== undefined;
          const versionHasProperty = version?.property !== null && version?.property !== undefined;

          isDeletion = Boolean(baseHasProperty && !versionHasProperty);
          isAddition = Boolean(!baseHasProperty && versionHasProperty);
          isModified = Boolean(
            baseHasProperty &&
            versionHasProperty &&
            baseProperty &&
            version?.property &&
            !arePropertyValuesEqual(baseProperty, version.property)
          );
        }
      }
      // If version is null, all change flags remain false (property is unchanged)

      // For deletions, show red tag
      if (isDeletion) {
        return (
          <Tag type="red" className="shrink-0">
            {t("caseVersions.diff.property.removed")}
          </Tag>
        );
      }

      // For additions, show tag next to value
      if (isAddition && formattedValue) {
        return (
          <div className="flex items-center gap-2 min-w-0 w-full">
            <div className="min-w-0 flex-1 overflow-hidden">
              <TableTruncate label={formattedValue}>{formattedValue}</TableTruncate>
            </div>
            <Tag type="green" className="shrink-0 whitespace-nowrap">
              {t("caseVersions.diff.property.new")}
            </Tag>
          </div>
        );
      }

      // For modifications, show blue text
      if (isModified && formattedValue) {
        return (
          <TableTruncate className="text-textHighlight" label={formattedValue}>
            {formattedValue}
          </TableTruncate>
        );
      }

      // No change or no value
      if (formattedValue) {
        return <TableTruncate label={formattedValue}>{formattedValue}</TableTruncate>;
      }

      return null;
    },
    [versions, formatPropertyValue, t]
  );

  // Build columns dynamically
  const columns: NullableTableColumns<CasePropertyDiff<string>> = useMemo(() => {
    const cols: NullableTableColumns<CasePropertyDiff<string>> = {
      property: {
        label: t("cases.table.property.column.title"),
        width: 1,
        flexGrow: 1,
        flexShrink: 1,
        render: (row) => getPropertyName(row),
      },
    };

    // First column: baseline (first version in the array)
    cols[versions[0].key] = {
      label: versions[0].customHeader || versions[0].label,
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      render: (row) => {
        // For additions, show empty cell in baseline column
        if (!row.original.property) {
          return null;
        }
        const formattedValue = formatPropertyValue(row.original.property);
        // Baseline column shows only the value, no tags
        return <TableTruncate label={formattedValue}>{formattedValue}</TableTruncate>;
      },
    };

    // Add a column for each version after the baseline (starting from index 1)
    for (let i = 1; i < versions.length; i++) {
      const versionConfig = versions[i];
      if (versionConfig.case) {
        cols[versionConfig.key] = {
          label: versionConfig.customHeader || versionConfig.label,
          width: 1,
          flexGrow: 1,
          flexShrink: 1,
          render: (row) => renderVersionCell(row, versionConfig.key, i),
        };
      }
    }

    return cols;
  }, [versions, formatPropertyValue, t, renderVersionCell]);

  return (
    <PassiveModal
      title={title}
      subtitle={subtitle}
      fullHeight
      size="lg"
      onClose={onClose}
      initialFocus={`#${searchInputId}`}
    >
      <FixedTable
        id={tableId}
        columns={columns}
        data={rows}
        isLoading={false}
        pageSize={10}
        filter={(row) => searchInCasePropertyDiff(searchTerm, row)}
        toolbar={() => (
          <TableToolbar aria-label={t("caseVersions.diff.table.toolbar.label")}>
            <TableToolbarContent>
              <TableToolbarSearch
                id={searchInputId}
                search={searchTerm}
                onSearchChange={setSearchTerm}
              />
            </TableToolbarContent>
          </TableToolbar>
        )}
      />
    </PassiveModal>
  );
};
